import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ScenarioRunState } from '../../../api/api';
import { runDuration, runMoment, runTime } from '../../../utils/scenarioRunFormat';
import { Outcome, outcomeOfScenario } from '../ScenarioRunTable';
import { runCallsLabel, runCallsTitle } from './exactCallsModel';
import { PhaseBar } from './PhaseBar';
import { isInRun, runPhases } from './runDetailModel';

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * The verdict of the run in one sentence, the counts that make it up, and where the time went.
 *
 * Everything here is about the scenarios the run executed. Those the run filter left out are in the state document
 * so the suite reads complete, and counting them made a run of one scenario look like a run of forty-seven.
 */
export const RunSummary = ({
    state,
    finished,
    live,
}: {
    state: ScenarioRunState;
    finished: boolean;
    live: boolean;
}) => {
    const scenarios = state.scenarios ?? [];
    const inRun = scenarios.filter(isInRun);
    const outcomes = inRun.map((scenario) => outcomeOfScenario(scenario).outcome as Outcome);
    const count = (...kinds: Outcome[]) => outcomes.filter((outcome) => kinds.includes(outcome)).length;
    const notInRun = scenarios.length - inRun.length;
    const runEvents = (state.events ?? []).filter((event) => !event.scenarioId);
    const runErrors = runEvents.filter((event) => event.level === 'error').length;
    const runWarnings = runEvents.filter((event) => event.level === 'warning').length;

    const failed = count('failed');
    const passed = count('passed');
    const warned = count('warnings');

    const kind = !finished ? 'busy' : failed > 0 || runErrors > 0 ? 'failed' : warned > 0 ? 'warning' : 'passed';
    const icon: IconProp = !finished
        ? 'spinner'
        : kind === 'failed'
        ? 'circle-xmark'
        : kind === 'warning'
        ? 'triangle-exclamation'
        : 'circle-check';

    const headline = !finished
        ? `Running, ${count('passed', 'warnings', 'failed')} of ${plural(inRun.length, 'scenario')} done`
        : failed > 0
        ? `${failed} of ${plural(inRun.length, 'scenario')} failed`
        : inRun.length === 0
        ? 'No scenario ran'
        : warned > 0
        ? `${plural(passed + warned, 'scenario')} passed, ${warned} with warnings`
        : `${inRun.length === 1 ? 'The scenario' : `All ${inRun.length} scenarios`} passed`;

    const counts = [
        { label: 'failed', value: failed, kind: 'failed' },
        { label: 'passed', value: passed, kind: 'passed' },
        { label: 'with warnings', value: warned, kind: 'warning' },
        { label: 'running', value: count('running'), kind: 'busy' },
        { label: finished ? 'not run' : 'to go', value: count('pending', 'skipped'), kind: 'idle' },
        { label: 'run errors', value: runErrors, kind: 'failed' },
        { label: 'run warnings', value: runWarnings, kind: 'warning' },
    ].filter((entry) => entry.value > 0);

    return (
        <section className={`rd-summary rd-summary--${kind}`}>
            <div className="rd-summary__verdict">
                <FontAwesomeIcon icon={icon} spin={!finished} className="rd-summary__icon" />
                <div>
                    <div className="rd-summary__headline">{headline}</div>
                    <div className="rd-summary__sub">
                        {finished ? (
                            <>
                                Finished {runMoment(state.finishedAtUtc)}, after{' '}
                                {runDuration(state.startedAtUtc, state.finishedAtUtc)}
                            </>
                        ) : (
                            <>
                                {state.currentActivity}
                                <span className="rd-summary__fresh">
                                    {live ? (
                                        <span className="rd-live">
                                            <span className="rd-live__dot" /> live
                                        </span>
                                    ) : (
                                        'refreshed'
                                    )}{' '}
                                    {runTime(state.updatedAtUtc)}
                                </span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <ul className="rd-summary__counts">
                {counts.map((entry) => (
                    <li key={entry.label} className={`rd-count rd-count--${entry.kind}`}>
                        <span className="rd-count__value">{entry.value}</span> {entry.label}
                    </li>
                ))}
                {state.exactCalls && (
                    <li className="rd-count rd-count--muted" title={runCallsTitle(state.exactCalls)}>
                        {runCallsLabel(state.exactCalls)}
                    </li>
                )}
                {notInRun > 0 && (
                    <li className="rd-count rd-count--muted" title="Left out by the run filter or disabled in the suite">
                        {notInRun} not in this run
                    </li>
                )}
            </ul>

            <div className="rd-summary__time">
                <h3 className="rd-part__title">Where the time went</h3>
                <PhaseBar phases={runPhases(state)} />
            </div>
        </section>
    );
};
